import React, { useCallback } from "react";
import { graphql, type PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { faExclamationTriangle, faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { trackConversion } from "~/lib/ab/growthbook";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { getFirstUserFacingError } from "~/lib/utils";
import { Button } from "~/ui";
import Checkbox from "~/ui/legacy/Checkbox";
import { HelpCenterIcon, Modal } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import { AGENDA_BUILDER_FEATURE_FLAG } from "../agendaBuilder";
import { applyTemplate } from "./noteTemplateGql";
import { SmartTemplateAuthor } from "./TemplateAuthor";

import type { ApplyModalQuery as ApplyModalQueryType } from "~/__generated__/ApplyModalQuery.graphql";

import styles from "./css/ApplyModal.module.css";

const ApplyModalQuery = graphql`
	query ApplyModalQuery($templateId: ID!, $noteId: ID!) {
		template: node(id: $templateId) {
			... on NoteTemplateNode {
				id
				title
				summary
				author {
					...TemplateAuthor_Author
				}
			}
		}
		note: node(id: $noteId) {
			... on Note {
				stream {
					template {
						id
					}
				}
			}
		}
	}
`;

interface QueryComponentProps {
	queryRef: PreloadedQuery<ApplyModalQueryType>;
}

function TemplateSummary({ queryRef }: QueryComponentProps) {
	const { template } = usePreloadedQuery<ApplyModalQueryType>(ApplyModalQuery, queryRef);

	if (!template) throw new Error("Invalid template ID");

	return (
		<article className={styles.summary}>
			{template.author !== undefined && <SmartTemplateAuthor author={template.author} />}
			<h1 className={styles.summaryTitle} data-test-id="note-template-summary-title">
				{template.title}
			</h1>
			<p className={styles.summaryDescription} data-test-id="note-template-summary-description">
				{template.summary}
			</p>
		</article>
	);
}

function DefaultTemplateCheckBox({
	queryRef,
	checkboxRef,
	templateId,
}: QueryComponentProps & { checkboxRef: React.RefObject<HTMLInputElement>; templateId: string }) {
	const { note } = usePreloadedQuery<ApplyModalQueryType>(ApplyModalQuery, queryRef);

	if (note?.stream?.template?.id === templateId) return null;

	return (
		<label className={styles.checkboxContainer} data-test-id="note-template-apply-set-default">
			<Checkbox royal innerRef={checkboxRef} className={styles.checkbox} />
			<p className={styles.checkboxLabelText}>Save as the default template for this series</p>
		</label>
	);
}

export function ApplyTemplateModal({
	noteId,
	noteVersion,
	templateId,
	close,
	stream,
	templateTitle,
	fromTemplatesSection = false,
}: {
	noteId: string;
	noteVersion: number;
	templateId: string;
	fromTemplatesSection?: boolean;
	stream?: { displayName: string };
	templateTitle?: string;
	close: (confirmed?: boolean) => void;
}) {
	const [loading, setLoading] = React.useState(false);
	const checkboxRef = React.useRef<HTMLInputElement>(null);

	const [queryRef, loadQuery] = useQueryLoader<ApplyModalQueryType>(ApplyModalQuery);

	const closeModal = React.useCallback(() => close(), [close]);

	React.useEffect(() => {
		dispatchAnalyticsTrack("[Use Template] Apply Template Modal Opened", {
			templateId,
			fromTemplatesSection,
		});
		loadQuery({ templateId, noteId }, { fetchPolicy: "store-and-network" });
	}, [loadQuery, templateId, noteId, fromTemplatesSection]);

	const doApplyTemplate = useCallback(async () => {
		try {
			setLoading(true);
			const makeDefault = checkboxRef.current?.checked ?? false;
			await applyTemplate({
				noteId,
				templateId,
				makeDefault,
			});
			const analyticsAttributes: Record<string, unknown> = {
				templateId,
				fromTemplatesSection,
				makeDefault,
			};
			if (stream) {
				analyticsAttributes["streamName"] = stream.displayName;
			}
			if (templateTitle) {
				analyticsAttributes["templateTitle"] = templateTitle;
			}
			dispatchAnalyticsTrack("[Use Template] Template Applied", analyticsAttributes);
			close(true);
		} catch (errors) {
			const userFacingError = getFirstUserFacingError(errors) ?? "Something went wrong, please try again.";

			toast.error(userFacingError, {
				title: "Failed to apply template",
			});
		} finally {
			setLoading(false);
			trackConversion(AGENDA_BUILDER_FEATURE_FLAG);
		}
	}, [noteId, templateId, fromTemplatesSection, stream, templateTitle, close]);

	return (
		<Modal onClose={closeModal} width="28.5rem" data-test-id="note-template-apply-modal">
			<Modal.Header>
				<Modal.Title>Apply Template</Modal.Title>
				<Modal.HeaderActions onClose={closeModal}>
					<HelpCenterIcon articleId="3808993" title="Set a template for a recurring meeting" />
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body>
				{queryRef && (
					<React.Suspense fallback={null}>
						<TemplateSummary queryRef={queryRef} />
						<DefaultTemplateCheckBox queryRef={queryRef} checkboxRef={checkboxRef} templateId={templateId} />
					</React.Suspense>
				)}
				<p className={styles.warningMessage}>
					<FontAwesomeIcon icon={noteVersion == 0 ? faInfoCircle : faExclamationTriangle} />
					<div>
						{noteVersion > 0 && "Applying this template will override the content of the current note."} Other notes in
						this series will not be affected.
					</div>
				</p>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="secondary" onClick={closeModal} className={styles.button}>
					Go back
				</Button>
				<Button
					size="normal"
					variant="primary"
					onClick={doApplyTemplate}
					disabled={loading}
					className={styles.button}
					data-test-id="note-template-apply-modal-confirm"
				>
					Apply template
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
